"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { 
  CheckCircleIcon, 
  ChatBubbleBottomCenterTextIcon, 
  ClockIcon, 
  UserGroupIcon, 
  SparklesIcon,
  RocketLaunchIcon,
  ServerIcon,
  HeartIcon,
  BoltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

export default function Solution() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromLeft = {
    hidden: { opacity: 0, x: isMobile ? -20 : -50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromRight = {
    hidden: { opacity: 0, x: isMobile ? 20 : 50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  };

  const solutions = [
    {
      icon: BoltIcon,
      title: "Réponses automatiques instantanées",
      description: "ConnectRH répond automatiquement aux questions RH les plus fréquentes, 24h/24 et 7j/7.",
      gradient: "from-yellow-500 to-orange-500",
      bgGradient: "from-yellow-50 to-orange-50",
      borderColor: "border-yellow-200",
      hoverBorder: "hover:border-yellow-400",
      animation: slideInFromLeft
    },
    {
      icon: UserGroupIcon,
      title: "Accompagnement personnalisé",
      description: "Chaque collaborateur bénéficie d'un accompagnement adapté tout au long de son parcours.",
      gradient: "from-purple-500 to-pink-500",
      bgGradient: "from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
      hoverBorder: "hover:border-purple-400",
      animation: scaleIn
    },
    {
      icon: ServerIcon,
      title: "Base de données centralisée",
      description: "S'appuie sur une base de données interne complète d'informations RH de votre entreprise.",
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      hoverBorder: "hover:border-blue-400",
      animation: slideInFromRight
    },
    {
      icon: SparklesIcon,
      title: "Libération des équipes RH",
      description: "Automatise les demandes simples pour permettre aux RH de se concentrer sur l'essentiel.",
      gradient: "from-emerald-500 to-teal-500",
      bgGradient: "from-emerald-50 to-teal-50",
      borderColor: "border-emerald-200",
      hoverBorder: "hover:border-emerald-400",
      animation: slideInFromLeft
    },
    {
      icon: HeartIcon,
      title: "Expérience collaborateur améliorée",
      description: "Améliore significativement la satisfaction et l'expérience des collaborateurs.",
      gradient: "from-rose-500 to-pink-500",
      bgGradient: "from-rose-50 to-pink-50",
      borderColor: "border-rose-200",
      hoverBorder: "hover:border-rose-400",
      animation: scaleIn
    }
  ];

  const benefits = [
    {
      icon: ShieldCheckIcon,
      number: "90%",
      label: "Réduction des questions répétitives",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: BoltIcon,
      number: "24/7",
      label: "Disponibilité continue",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: HeartIcon,
      number: "95%",
      label: "Satisfaction utilisateurs",
      gradient: "from-purple-500 to-pink-500"
    }
  ];

  return (
    <section id="solution" className={`py-16 bg-gradient-to-br from-emerald-50/30 via-cyan-50/20 to-blue-50/30 relative overflow-hidden smooth-scroll-section ${isMobile ? 'mobile-simple' : ''}`}>
      {/* Éléments décoratifs animés - désactivés sur mobile */}
      {!isMobile && (
        <>
          {/* Cercles décoratifs supprimés */}
        </>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
          className="text-center"
        >
          {/* Badge section */}
          <motion.div
            variants={scaleIn}
            whileHover={{ scale: 1.05 }}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-full px-6 py-3 mb-8 shadow-lg backdrop-blur-sm"
          >
            <motion.div 
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center"
            >
              <CheckCircleIcon className="w-4 h-4 text-white" />
            </motion.div>
            <span className="text-sm font-semibold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              Notre solution
            </span>
          </motion.div>

          {/* Titre principal */}
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-relaxed py-4"
            style={{ lineHeight: '1.3' }}
          >
            <motion.span
              animate={{ 
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "linear" 
              }}
              className="inline-block bg-gradient-to-r from-gray-900 via-green-600 to-gray-900 bg-[length:200%_100%] bg-clip-text text-transparent"
            >
              ConnectRH transforme
            </motion.span>
            <span className="block bg-gradient-to-r from-violet-600 to-purple-600 gradient-text-safe mt-2">
              votre gestion RH
            </span>
          </motion.h2>

          {/* Description */}
          <motion.div
            variants={fadeInUp}
            className="max-w-3xl mx-auto mb-16"
          >
            <p className="text-xl text-gray-600 leading-relaxed">
              Une solution intelligente qui{" "}
              <span className="font-semibold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                révolutionne l'expérience RH
              </span>{" "}
              en automatisant les processus répétitifs et en offrant un support continu à vos collaborateurs.
            </p>
          </motion.div>

          {/* Grille des solutions */}
          <motion.div
            variants={staggerContainer}
            className="max-w-6xl mx-auto"
          >
            {/* Première ligne - 2 éléments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {solutions.slice(0, 2).map((solution, index) => (
                <motion.div
                  key={index}
                  variants={solution.animation}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                  className={`
                    bg-gradient-to-br ${solution.bgGradient} rounded-3xl p-8 border-2 ${solution.borderColor} ${solution.hoverBorder}
                    transition-all duration-300 hover:shadow-2xl relative overflow-hidden group
                  `}
                >
                  {/* Effet de brillance au survol */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  <div className="flex flex-col items-center text-center relative">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className={`w-20 h-20 bg-gradient-to-r ${solution.gradient} rounded-3xl flex items-center justify-center mb-6 shadow-xl`}
                    >
                      <solution.icon className="w-10 h-10 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {solution.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-center">
                      {solution.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Deuxième ligne - 3 éléments */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {solutions.slice(2).map((solution, index) => (
                <motion.div
                  key={index + 2}
                  variants={solution.animation}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                  className={`
                    bg-gradient-to-br ${solution.bgGradient} rounded-3xl p-6 border-2 ${solution.borderColor} ${solution.hoverBorder}
                    transition-all duration-300 hover:shadow-2xl relative overflow-hidden group
                  `}
                >
                  {/* Effet de brillance au survol */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  <div className="flex flex-col items-center text-center relative">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      className={`w-16 h-16 bg-gradient-to-r ${solution.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}
                    >
                      <solution.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      {solution.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-center">
                      {solution.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
} 